import formatMediaObject from '../utils/formatters/format-media-object';
import { formatDietaryLabels } from './dietary-labels';

/**
 * Format the breadcrumbs for the product page.
 *
 * @param {object} context
 * @param {object} attributes
 *
 * @returns {({text: string, to: string}|{active: boolean, text: string})[]}
 */
export const formatBreadcrumbsForProductPage = (context, attributes) => {
    const product = attributes.product;
    const category = product
        ?.category;

    return [
        {
            text: context.$snippet('home'),
            to: '/',
        },
        {
            text: context.$snippet('menu'),
            to: context.$snippet('menu_page_link'),
        },
        {
            text: category?.name,
            to: category?.page?.uri,
        },
        {
            text: product?.marketing_title,
            active: true,
        },
    ].filter(({ to, active }) => to || active);
};

/**
 * Format the nutrition facts for the product page.
 *
 * @param {object} product
 * @param {object} options
 * @param {boolean} options.hasPdfLink
 *
 * @returns {{description: string, title: string}[]}
 */
export function formatNutritionFacts(product, { hasPdfLink = false } = {}) {
    // The keys are the keys of the product object and the values are the titles of the nutrition facts.
    const nutritionFactKeys = {
        calories: this.$snippet('nutrition_calories', 'energy (kcal)'),
        fat: this.$snippet('nutrition_fat', 'fat (g)'),
        fat_saturated: this.$snippet('nutrition_fat_saturated', 'of which saturates (g)'),
        protein: this.$snippet('nutrition_protein', 'protein (g)'),
        carbs: this.$snippet('nutrition_carbs', 'carbohydrates (g)'),
        sugars: this.$snippet('nutrition_sugars', 'of which sugars (g)'),
        fibre: this.$snippet('nutrition_fibre', 'fibre (g)'),
        salt: `${ this.$snippet('nutrition_salt', 'salt (g)') }${ hasPdfLink ? '*' : '' }`,
        plant_points: this.$snippet('nutrition_plant_points', 'plant points'),
    };

    // Only return the nutrition facts that are present in the product object.
    return Object.keys(nutritionFactKeys).flatMap((key) => {
        if (product[key]) {
            return {
                title: nutritionFactKeys[key],
                description: product[key],
            };
        }

        return [];
    });
};

/**
 * Format the allergens for the product page.
 *
 * @param {Array} allergens
 *
 * @returns {string}
 */
export const formatAllergens = (allergens = []) => {
    return allergens.map(({ name }) => name)
        .join(', ')
        .toLowerCase();
};

/**
 * Format the data to be passed to the ProductPage component.
 *
 * @param {object} context
 * @param {object} attributes
 *
 * @returns {object}
 */
export const formatDataForProductPage = (context, attributes) => {
    const product = attributes.product;
    const category = product
        ?.category;

    return {
        category: {
            text: category?.name,
            to: category?.page?.uri,
        },
        name: product?.marketing_title,
        description: product?.marketing_product_description,
        calories: `${ product?.calories } kcal`,
        image: formatMediaObject.call(context, product?.website_product_image),
        imageSm: formatMediaObject.call(context, product?.website_product_image),
        dietaryLabels: formatDietaryLabels(context, product?.dietary_labels),
        contains: formatAllergens(product?.allergens),
        allergenWarning: context.$snippet('menu_allergens_text'),
        mayContain: formatAllergens(product?.may_contain),
        nutritionFacts: formatNutritionFacts.call(context, product, {
            hasPdfLink: Boolean(attributes.pdf_button_text && attributes.pdf_button_file),
        }),
        ingredients: product?.ingredients,
        storyTitle: attributes.productPageTemplate_story_title,
        storyContent: attributes.productPageTemplate_story_content,
        heroButtonLink: context.$snippet('find_an_itsu_link'),
        heroButtonText: context.$snippet('find_an_itsu', 'find an itsu'),
        readMore: {
            buttonText: attributes.read_more?.button_text,
            content: attributes.read_more?.content,
        },
        pdfButtonText: attributes.pdf_button_text,
        pdfButtonLink: formatMediaObject.call(context, attributes.pdf_button_file).src,
        showSoySauceModalButton: attributes.show_soy_sauce_modal_button,
        plantPoints: product?.plant_points ? {
            points: product?.plant_points,
            description: product?.plant_points_description,
            image: formatMediaObject.call(context, product?.plant_points_image),
            background: product?.plant_points_background,
        } : {},
    };
};
